import { Fragment, useEffect, useMemo } from 'react';
import { SelectColor, SelectColorSize } from '~/components/ui/SelectColor';
import { Slider } from '~/components/ui/Slider';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { PedOverlaySection } from '~/shared';
import {
	setOpacity as setOpacityAction,
	setPrimaryColor as setPrimaryColorAction,
	setSecondaryColor as setSecondaryColorAction,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { IListItem } from '../ListTab';
import { StyledContainer } from './SelectColorPreview.styled';

const SelectColorPreview = () => {
	const dispatch = useLayoutDispatch();
	const items = useObservable(rage.barberShop.getItems());
	const activeList = useLayoutSelector((state) => state.barberShop.activeList);
	const activeListIndex = useLayoutSelector(
		(state) => state.barberShop.activeListIndex,
	);
	const sections = useObservable(rage.barberShop.getSections());
	const activeCategory = useLayoutSelector(
		(state) => state.barberShop.activeCategory,
	);
	const currentAppearance = useObservable(
		rage.barberShop.getCurrentAppearance(),
	);
	const primaryColor = useLayoutSelector(
		(state) => state.barberShop.primaryColor,
	);
	const secondaryColor = useLayoutSelector(
		(state) => state.barberShop.secondaryColor,
	);
	const opacity = useLayoutSelector((state) => state.barberShop.opacity);

	const activeSection = useMemo(() => {
		return sections?.find((section) => section.id === activeCategory);
	}, [sections, activeCategory]);

	const item = useMemo(() => {
		const active = activeList[activeListIndex || 0] as IListItem | undefined;
		if (
			!active ||
			active.overlaySection === undefined ||
			active.index === undefined
		)
			return undefined;
		return items?.find(
			(i) =>
				i.overlaySection === active.overlaySection && i.index === active.index,
		);
	}, [items, activeList, activeListIndex]);

	const hairColors = useObservable(rage.barberShop.getHairColors());
	const makeupColors = useObservable(rage.barberShop.getMakeupColors());

	const isHairSection = (section?: PedOverlaySection) => {
		return (
			section === PedOverlaySection.Hair ||
			section === PedOverlaySection.FacialHair ||
			section === PedOverlaySection.Eyebrows ||
			section === PedOverlaySection.BodyHair
		);
	};

	const palette = useMemo(() => {
		const section = item?.overlaySection;
		return isHairSection(section) ? hairColors : makeupColors;
	}, [item, hairColors, makeupColors]);

	const colorItems = useMemo(() => palette?.map((c) => c.hex) ?? [], [palette]);

	useEffect(() => {
		const section = item?.overlaySection;
		const installedOverlay = currentAppearance?.overlays.find(
			(o) => o.section === section,
		);

		if (installedOverlay && installedOverlay.index === item?.index) {
			const paletteToUse = palette ?? [];
			const primaryIdx = installedOverlay?.colorPrimary ?? undefined;
			const secondaryIdx = installedOverlay?.colorSecondary ?? undefined;
			const primaryHex =
				paletteToUse.find((c) => c.index === primaryIdx)?.hex ?? colorItems[0];
			const secondaryHex =
				paletteToUse.find((c) => c.index === secondaryIdx)?.hex ??
				colorItems[0];
			dispatch(setPrimaryColorAction(primaryHex));
			dispatch(setSecondaryColorAction(secondaryHex));
			dispatch(setOpacityAction(installedOverlay?.colorOpacity ?? 1));
			return;
		}
	}, [item, palette, currentAppearance, colorItems]);

	return (
		<StyledContainer>
			{activeSection?.withColor && (
				<Fragment>
					<Text>Первый цвет</Text>
					<SelectColor
						value={primaryColor}
						items={colorItems}
						size={SelectColorSize.Size30}
						count={5}
						onChange={(value) => dispatch(setPrimaryColorAction(value))}
					/>
					<Text>Дополнительный цвет</Text>
					<SelectColor
						value={secondaryColor}
						items={colorItems}
						size={SelectColorSize.Size30}
						count={5}
						onChange={(value) => dispatch(setSecondaryColorAction(value))}
					/>
				</Fragment>
			)}
			{activeSection?.withOpacity && (
				<Fragment>
					<Text>Интенсивность цвета</Text>
					<Slider
						value={opacity}
						min={0}
						max={1}
						onChange={(value) => dispatch(setOpacityAction(value))}
						step={0.01}
						width={280}
					/>
				</Fragment>
			)}
		</StyledContainer>
	);
};

export { SelectColorPreview };
